<script setup lang="ts">
import {
	ref,
	reactive,
	computed,
	onMounted,
	onBeforeUnmount,
	nextTick,
	useSlots,
} from 'vue'
import { debounce } from 'radashi'

const props = defineProps({
	custPerPage: {
		type: Number,
		default: 0,
	},
	disableWrapWidth: {
		type: Boolean,
		default: false,
	},
	isHorizonPadding: {
		type: Boolean,
		default: false,
	},
})

const slots = useSlots()

const isShow = reactive({
	arrowLeft: false,
	arrowRight: false,
	maskLeft: false,
	maskRight: false,
})
const isTablet = ref(window.matchMedia('(min-width: 768px)').matches)
const isDesktop = ref(window.matchMedia('(min-width: 1200px)').matches)
const index = ref(0)
const tabsLength = ref(0)
const tabScroll = ref<HTMLElement | null>(null)

const perPage = computed(() => {
	if (props.custPerPage > 0) {
		return props.custPerPage
	}
	return isDesktop.value ? 5 : 3
})

const wrapWidthStyle = computed(() => {
	if (props.disableWrapWidth) return { width: undefined }
	let width
	if (tabsLength.value < perPage.value) {
		width = tabsLength.value * (isDesktop.value ? 185 : 160)
	} else {
		width = perPage.value * (isDesktop.value ? 185 : 160)
	}
	return { width: isTablet.value ? `${width}px` : undefined }
})

const position = computed(() => {
	const basePosition = isDesktop.value ? 185 : 160
	const padding = isDesktop.value && props.isHorizonPadding ? 16 + 16 : 0
	return (basePosition + padding) * index.value
})

const scrollStyle = computed(() => {
	return {
		transform: isTablet.value ? `translateX(-${position.value}px)` : undefined,
	}
})

const isSlotFixExist = computed(() => {
	return !!slots.fix
})

function resetTabs() {
	index.value = 0
	nextTick(() => {
		if (slots.default) {
			tabsLength.value = slots.default().filter(v => v.type).length
		}
		controllArrowShowOrHide()
	})
}

function setTabsLength() {
	if (slots.default) {
		tabsLength.value = slots.default().filter(v => v.type).length
	}
}

function controllArrowShowOrHide() {
	const scrollElement = tabScroll.value
	if (!scrollElement) return
	isShow.maskLeft = scrollElement.scrollLeft !== 0
	if (isTablet.value) {
		isShow.maskRight = tabsLength.value - perPage.value > index.value
	} else {
		isShow.maskRight =
			scrollElement.scrollLeft + scrollElement.offsetWidth !==
			scrollElement.scrollWidth
	}
	isShow.arrowLeft = index.value !== 0
	isShow.arrowRight = index.value < tabsLength.value - perPage.value
}

function tabScrollTo(direction: 'left' | 'right') {
	if (direction === 'left') {
		if (index.value <= perPage.value) {
			index.value = 0
		} else {
			index.value -= perPage.value
		}
	}
	if (direction === 'right') {
		if (
			tabsLength.value - perPage.value - (tabsLength.value % perPage.value) <=
			index.value
		) {
			index.value = tabsLength.value - perPage.value
		} else {
			index.value += perPage.value
		}
	}

	controllArrowShowOrHide()
}

function tabScrollToIndex(targetIndex: number) {
	const slidePageTimes = Math.floor((targetIndex - index.value) / perPage.value)
	const slideDirection = slidePageTimes > 0 ? 'right' : 'left'
	for (let i = 0; i < Math.abs(slidePageTimes); i += 1) {
		tabScrollTo(slideDirection)
	}
}

onMounted(() => {
	resetTabs()
	tabScroll.value?.addEventListener(
		'scroll',
		debounce({ delay: 100 }, controllArrowShowOrHide),
	)

	window.addEventListener('resize', () => {
		isTablet.value = window.matchMedia('(min-width: 768px)').matches
		isDesktop.value = window.matchMedia('(min-width: 1200px)').matches
		resetTabs()
	})
})

onBeforeUnmount(() => {
	tabScroll.value?.removeEventListener(
		'scroll',
		debounce({ delay: 100 }, controllArrowShowOrHide),
	)
})

defineExpose({
	setTabsLength,
	tabScrollToIndex,
})
</script>

<template>
	<div
		class="bs-tabs"
		:class="{ 'bs-tabs--horizon-padding': isDesktop && isHorizonPadding }"
	>
		<transition name="opacity">
			<div
				class="bs-tabs-arrow-left"
				v-show="isShow.arrowLeft"
				@click="tabScrollTo('left')"
			>
				<i class="bs-icon">
					<span class="icon-tabs-arrow-left"></span>
				</i>
			</div>
		</transition>
		<div
			class="bs-tabs-scroll-wrap"
			:class="{ 'bs-tabs-scroll-wrap--hasFix': isSlotFixExist }"
			:style="wrapWidthStyle"
		>
			<div class="bs-tabs-scroll-mask-left" v-show="isShow.maskLeft"></div>
			<div class="bs-tabs-scroll-wrap" ref="tabScroll">
				<div class="bs-tabs-scroll" :style="scrollStyle">
					<div class="bs-tabs-scroll-inner">
						<slot />
					</div>
				</div>
			</div>
			<div class="bs-tabs-scroll-mask-right" v-show="isShow.maskRight"></div>
		</div>
		<!-- <div class="bs-tab bs-tab--fix" v-if="$slots.fix">
      <div class="bs-tab-inner">
        <slot name="fix" />
      </div>
    </div> -->
		<slot name="fix" class="bs-tab--fix" />
		<transition name="opacity">
			<div
				class="bs-tabs-arrow-right"
				v-show="isShow.arrowRight"
				@click="tabScrollTo('right')"
			>
				<i class="bs-icon">
					<span class="icon-tabs-arrow-right"></span>
				</i>
			</div>
		</transition>
	</div>
</template>
